/**
 * Les entretiens RÉALISÉS d'une machine, sous la carte « Prochain entretien » (lot B2).
 *
 * C'est l'ANCRE du calcul : sans entretien enregistré, une machine dont des points sont franchis reste « à
 * confirmer ». L'historique est en INSERTION SEULE, comme celui des compteurs : un entretien mal saisi se
 * RECTIFIE (une ligne de plus qui remplace l'ancienne) ou s'ANNULE (une ligne qui l'annule) ; il reste lisible,
 * grisé et barré. L'absence de bouton « supprimer » n'est pas un oubli.
 *
 * Le PALIER n'est jamais présélectionné : un bon d'intervention ne dit jamais quel palier a été fait, c'est
 * l'utilisateur qui le choisit. Le serveur décide de tout le reste (refus, doublons, relevé écrit en même temps).
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { useDroitsGammes } from '../auth/useDroits';
import { ApiError, api, type EntretienRealise, type ListeEntretiensRealises } from '../api/client';
import {
  LIBELLE_ETAT_LIGNE,
  confirmationAnnulation,
  etatLigne,
  heuresPourSaisie,
  libelleNiveau,
  peutReleverAussi,
  phraseEntretien,
  heuresTexte,
  lireHeuresEntretien,
  validerEntretien,
  type ErreursEntretien,
  type NiveauChoix,
} from '../domaine/entretiens-realises';
import { aujourdhuiLocal } from '../domaine/gammes-versions';
import { dateFr } from '../domaine/gammes';
import { Champ, ErreurFormulaire, Panneau } from './Formulaire';

/** Combien de lignes l'historique de la carte charge d'un coup. */
const LIGNES_CHARGEES = 100;

type Panneau_ = { readonly mode: 'nouveau' } | { readonly mode: 'rectifier'; readonly cible: EntretienRealise };

export function EntretiensRealises({
  assetId,
  niveaux,
  compteurDesigne,
  auCalendrier = false,
  cle,
  onChange,
}: {
  assetId: string;
  /** Les paliers de la version en vigueur de la gamme : le menu « palier réalisé ». */
  niveaux: readonly NiveauChoix[];
  /** Un compteur est désigné pour les entretiens : seules ses heures ont une échelle. */
  compteurDesigne: boolean;
  /** La machine se déclenche au calendrier : le premier entretien enregistré fera partir le rythme. */
  auCalendrier?: boolean;
  /** Change quand la carte se recharge : l'historique aussi. */
  cle: number;
  /**
   * Appelé après toute écriture : la carte « Prochain entretien » se recalcule, et la fiche rafraîchit les
   * compteurs (un entretien peut avoir écrit un relevé).
   */
  onChange: () => void | Promise<void>;
}) {
  const droits = useDroitsGammes();
  const [liste, setListe] = useState<ListeEntretiensRealises | null>(null);
  // Erreur PROPRE à ce bloc : celle de la carte est remise à zéro à chaque rechargement.
  const [erreur, setErreur] = useState<string | null>(null);
  const [messages, setMessages] = useState<readonly string[]>([]);
  const [panneau, setPanneau] = useState<Panneau_ | null>(null);

  const charger = useCallback(async () => {
    try {
      setListe(await api.entretiensRealises(assetId, LIGNES_CHARGEES));
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Historique des entretiens illisible.');
    }
  }, [assetId]);

  useEffect(() => {
    void charger();
  }, [charger, cle]);

  /** Après une écriture : relire l'historique, prévenir la fiche, puis annoncer ce que le serveur dit en plus. */
  const apresEcriture = async (messagesDuServeur: readonly string[]) => {
    await charger();
    await onChange();
    setMessages(messagesDuServeur);
  };

  const annuler = async (e: EntretienRealise) => {
    if (!window.confirm(confirmationAnnulation(e))) return;
    setMessages([]);
    try {
      const rep = await api.annulerEntretien(assetId, e.id);
      await apresEcriture(rep.messages);
    } catch (err) {
      // 409 : quelqu'un l'a déjà corrigé. On relit D'ABORD (le rechargement remet l'erreur à zéro), puis on annonce.
      if (err instanceof ApiError && err.status === 409) await charger();
      setErreur(err instanceof ApiError ? err.message : 'Annulation impossible.');
    }
  };

  const peutEcrire = droits.peutEnregistrerEntretien;

  return (
    <div className="entretiens-realises">
      <div className="entretiens-entete">
        <h4>Entretiens réalisés{liste ? ` (${liste.items.filter((i) => i.effectif).length})` : ''}</h4>
        {peutEcrire && (
          <button
            type="button"
            className="secondaire discret"
            disabled={niveaux.length === 0}
            title={niveaux.length === 0 ? 'La gamme n’a aucune version en vigueur : aucun palier à choisir.' : undefined}
            onClick={() => {
              setMessages([]);
              setPanneau({ mode: 'nouveau' });
            }}
          >
            Enregistrer un entretien réalisé
          </button>
        )}
      </div>

      {erreur && <div className="message erreur">{erreur}</div>}
      {messages.map((m) => (
        <div key={m} className="message info">
          {m}
        </div>
      ))}

      {liste && liste.items.length === 0 && (
        <div className="aide">
          {auCalendrier
            ? 'Aucun entretien réalisé n’est enregistré : enregistrez le dernier fait, le rythme partira de sa date.'
            : 'Aucun entretien réalisé n’est enregistré. Tant qu’aucun ne l’est, les points déjà franchis par le compteur restent « à confirmer ».'}
        </div>
      )}

      {liste && liste.items.length > 0 && (
        <table className="tableau-entretiens">
          <thead>
            <tr>
              <th>Entretien</th>
              <th>Observation</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {liste.items.map((e) => {
              const etat = etatLigne(e);
              return (
                <tr key={e.id} className={etat === 'EFFECTIF' ? undefined : 'ligne-remplacee'}>
                  <td>
                    <strong>{phraseEntretien(e)}</strong>
                    {e.readingId && <div className="aide">Relevé du compteur écrit avec cet entretien.</div>}
                    {e.interventionId && (
                      <div className="aide">
                        Enregistré par la validation d’un <Link to={`/interventions?bon=${e.interventionId}`}>bon d’intervention</Link>.
                      </div>
                    )}
                  </td>
                  <td>
                    {etat !== 'EFFECTIF' && <div className="aide">{LIBELLE_ETAT_LIGNE[etat]}.</div>}
                    {e.correctsRecordId && <div className="aide">Rectifie un entretien précédent.</div>}
                    {e.note && <div className="aide">{e.note}</div>}
                    {e.annulation?.note && <div className="aide">Annulation : {e.annulation.note}</div>}
                  </td>
                  <td className="actions-ligne">
                    {peutEcrire && e.effectif && (
                      <>
                        <button
                          type="button"
                          className="secondaire discret"
                          title="Ajoute un entretien qui remplace celui-ci : l’ancien reste visible, rien ne s’efface"
                          onClick={() => {
                            setMessages([]);
                            setPanneau({ mode: 'rectifier', cible: e });
                          }}
                        >
                          Rectifier
                        </button>
                        <button
                          type="button"
                          className="secondaire discret danger-discret"
                          title="Il reste lisible dans l’historique, marqué « annulé »"
                          onClick={() => void annuler(e)}
                        >
                          Annuler cet entretien
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {liste && liste.total > liste.items.length && (
        <div className="aide">
          {liste.items.length} entretiens affichés sur {liste.total}.
        </div>
      )}

      {panneau && (
        <FormulaireEntretien
          assetId={assetId}
          niveaux={niveaux}
          compteurDesigne={compteurDesigne}
          panneau={panneau}
          onFermer={() => setPanneau(null)}
          onEnregistre={async (rep) => {
            setPanneau(null);
            await apresEcriture(rep);
          }}
          onConflit={charger}
        />
      )}
    </div>
  );
}

/** Le panneau de saisie : un entretien neuf, ou la rectification d'un entretien (mêmes champs, préremplis). */
function FormulaireEntretien({
  assetId,
  niveaux,
  compteurDesigne,
  panneau,
  onFermer,
  onEnregistre,
  onConflit,
}: {
  assetId: string;
  niveaux: readonly NiveauChoix[];
  compteurDesigne: boolean;
  panneau: Panneau_;
  onFermer: () => void;
  onEnregistre: (messages: readonly string[]) => Promise<void>;
  onConflit: () => Promise<void>;
}) {
  const droits = useDroitsGammes();
  const aujourdhui = aujourdhuiLocal();
  const cible = panneau.mode === 'rectifier' ? panneau.cible : null;

  // Le palier d'un entretien qui se rectifie n'est repris que s'il existe encore dans la gamme en vigueur ;
  // sinon on laisse le choix vide : le palier se CHOISIT, il ne se devine pas.
  const paliers = niveaux.some((n) => n.levelKey === cible?.levelKey) ? (cible?.levelKey ?? '') : '';
  const [levelKey, setLevelKey] = useState(paliers);
  const [date, setDate] = useState(cible?.performedOn ?? aujourdhui);
  const [heures, setHeures] = useState(heuresPourSaisie(cible?.counterValue ?? null));
  const [note, setNote] = useState(cible?.note ?? '');
  // Cochée d'emblée quand elle s'applique (droit, compteur désigné, heures saisies) : un entretien et son relevé
  // sont le même geste. Le serveur relie un relevé identique déjà présent au lieu d'en créer un second.
  const [releverAussi, setReleverAussi] = useState(true);
  const [erreurs, setErreurs] = useState<ErreursEntretien>({});
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  // Une erreur de champ ne se recalcule qu'à l'envoi : sans ceci, « Choisissez le palier » resterait affiché
  // alors que le palier est choisi. On retire l'erreur d'un champ dès qu'on le modifie.
  const effacer = (cle: keyof ErreursEntretien) =>
    setErreurs((a) => Object.fromEntries(Object.entries(a).filter(([k]) => k !== cle)) as ErreursEntretien);

  const contexte = { aujourdhui, compteurDesigne, peutReleverCompteurs: droits.peutReleverCompteurs };
  const lectureHeures = lireHeuresEntretien(heures);
  const caseVisible = peutReleverAussi(contexte, lectureHeures);

  const soumettre = async (e: FormEvent) => {
    e.preventDefault();
    const validation = validerEntretien(
      { levelKey, date, heures, note, releverAussi: caseVisible && releverAussi },
      contexte,
      cible?.id,
    );
    if (!validation.ok) {
      setErreurs(validation.erreurs);
      return;
    }
    setErreurs({});
    setErreur(null);
    setEnCours(true);
    try {
      const rep = await api.enregistrerEntretien(assetId, validation.entree);
      await onEnregistre(rep.messages);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Enregistrement impossible.';
      // 409 : doublon, ou entretien déjà corrigé par quelqu'un d'autre. On relit D'ABORD (un rechargement remet
      // les erreurs à zéro ailleurs), puis on annonce dans le panneau.
      if (err instanceof ApiError && err.status === 409) await onConflit();
      setErreur(message);
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre={cible ? `Rectifier l’entretien du ${dateFr(cible.performedOn)}` : 'Enregistrer un entretien réalisé'}
      sousTitre={
        cible
          ? 'L’ancien entretien reste visible dans l’historique, marqué « remplacé » : rien ne s’efface.'
          : 'Ce que vous enregistrez ici sert de point de départ au calcul du prochain entretien.'
      }
      onFermer={onFermer}
      actions={
        <>
          <button type="button" className="secondaire" onClick={onFermer} disabled={enCours}>
            Fermer
          </button>
          <button type="submit" form="formulaire-entretien" disabled={enCours}>
            {enCours ? 'Enregistrement…' : cible ? 'Enregistrer la rectification' : 'Enregistrer'}
          </button>
        </>
      }
    >
      <form id="formulaire-entretien" onSubmit={(e) => void soumettre(e)}>
        <ErreurFormulaire message={erreur} />

        <Champ
          libelle="Palier réalisé"
          requis
          aide="Un bon d’intervention ne dit pas quel palier a été fait : c’est à vous de le choisir."
        >
          <select
            value={levelKey}
            onChange={(e) => {
              setLevelKey(e.target.value);
              effacer('palier');
            }}
            aria-label="Palier réalisé"
          >
            <option value="">— choisir le palier —</option>
            {niveaux.map((n) => (
              <option key={n.levelKey} value={n.levelKey}>
                {libelleNiveau(n)}
              </option>
            ))}
          </select>
          {erreurs.palier && <div className="message erreur">{erreurs.palier}</div>}
        </Champ>

        <Champ libelle="Date de l’entretien" requis>
          <input
            type="date"
            value={date}
            max={aujourdhui}
            onChange={(e) => {
              setDate(e.target.value);
              effacer('date');
            }}
            aria-label="Date de l’entretien"
          />
          {erreurs.date && <div className="message erreur">{erreurs.date}</div>}
        </Champ>

        <Champ
          libelle="Heures du compteur à ce moment"
          aide={
            compteurDesigne
              ? 'Les heures totales lues sur la machine (laissez vide si vous ne les avez pas). Elles servent à dire quels points sont soldés.'
              : 'Désignez d’abord le compteur des entretiens (heures totales) pour pouvoir saisir des heures.'
          }
        >
          <input
            type="text"
            inputMode="decimal"
            value={heures}
            disabled={!compteurDesigne}
            placeholder="ex. 22 577"
            onChange={(e) => {
              setHeures(e.target.value);
              effacer('heures');
            }}
            aria-label="Heures du compteur à ce moment"
          />
          {erreurs.heures && <div className="message erreur">{erreurs.heures}</div>}
        </Champ>

        {caseVisible && (
          <label className="case-releve">
            <input type="checkbox" checked={releverAussi} onChange={(e) => setReleverAussi(e.target.checked)} />{' '}
            Relever aussi le compteur
            <span className="aide">
              {' '}
              (écrit un relevé de ces heures à cette date ; si le même relevé existe déjà, il est relié, pas dupliqué)
            </span>
          </label>
        )}

        <Champ libelle="Observation">
          <input
            type="text"
            value={note}
            maxLength={500}
            placeholder="Facultatif"
            onChange={(e) => setNote(e.target.value)}
            aria-label="Observation de l’entretien"
          />
        </Champ>

        {cible && (
          <p className="aide">
            Entretien rectifié : {phraseEntretien(cible)}
            {cible.counterValue !== null ? ` (heures lues : ${heuresTexte(cible.counterValue)})` : ''}.
          </p>
        )}
      </form>
    </Panneau>
  );
}
